import { defineStore } from 'pinia'
import { api } from '../services'
import type { Customer } from '../types'

// Create type for the UserStore return
export type UserStore = ReturnType<typeof useUserStore>

export const useUserStore = defineStore('user', {
  state: () => ({
    user: null as Customer | null,
    isLoading: false,
    availableUsers: [] as Customer[]
  }),

  getters: {
    userPoints: (state): number => state.user?.points || 0,
    userSegment: (state): string => {
      if (!state.user?.tags?.segment) return 'standard'
      return state.user.tags.segment
    },
    isAdvancedUser: (state): boolean => {
      return state.user?.tags?.segment === 'advanced'
    },
    isProfessionalUser: (state): boolean => {
      return state.user?.tags?.segment === 'professional'
    }
  },

  actions: {
    async loadUser() {
      this.isLoading = true
      try {
        const userData = await api.user.getCurrentUser()
        this.user = userData
      } catch (error) {
        console.error('Error loading user:', error)
      } finally {
        this.isLoading = false
      }
    },

    async getAvailableUsers(): Promise<Customer[]> {
      try {
        if (this.availableUsers.length === 0) {
          this.availableUsers = await api.user.getAvailableUsers()
        }
        return this.availableUsers
      } catch (error) {
        console.error('Error fetching available users:', error)
        return []
      }
    },

    async switchUser(customerId: string) {
      this.isLoading = true
      try {
        const success = await api.user.switchCurrentUser(customerId)
        
        if (success) {
          // Reload user data - components will react automatically via Pinia reactivity
          await this.loadUser()
        }
        
        return success
      } catch (error) {
        console.error('Error switching user:', error)
        return false
      } finally {
        this.isLoading = false
      }
    },

    async updatePoints(newPoints: number): Promise<boolean> {
      if (!this.user) return false
      
      try {
        const success = await api.user.updatePoints(newPoints)
        if (success) {
          // Update points - components will react automatically via Pinia reactivity
          this.user.points = newPoints
        }
        return success
      } catch (error) {
        console.error('Error updating points:', error)
        return false
      }
    },

    async spendPoints(amount: number): Promise<boolean> {
      if (!this.user || (this.user.points || 0) < amount) {
        return false
      }

      try {
        const newPoints = (this.user.points || 0) - amount
        const success = await api.user.updatePoints(newPoints)
        
        if (success) {
          // Update points - components will react automatically via Pinia reactivity
          this.user.points = newPoints
        }
        
        return success
      } catch (error) {
        console.error('Error spending points:', error)
        return false
      }
    },

    async addPoints(amount: number): Promise<boolean> {
      if (!this.user) return false
      
      const currentPoints = this.user.points || 0
      return await this.updatePoints(currentPoints + amount)
    }
  }
}) 